<template>
  <div class="upload-wrapper">
    <vue-auto-dropzone
      v-show="!(!!isProcessing && !!showProgress)"
      id="dropzone"
      ref="documentsDropzone"
      v-slot="{ files, removeFile }"
      :class="{ 'no-border': !border, styled: includeStyling }"
      :include-styling="includeStyling"
      :options="dropzoneOptions"
      @processingmultiple="startedProcessing"
      @totaluploadprogress="uploadProgress"
      @uploadprogress="fileUploadProgress"
      @queuecomplete="uploadCompleted"
      @successmultiple="uploadSuccess"
      @success="uploadFileSuccess"
      @errormultiple="uploadError"
      @dragstart="dragStart"
      @dragover="onDrag"
      @dragend="dragEnd"
      @drop="drop"
      @sending="sendingEvent"
      @sendingmultiple="sendingMultipleEvent"
    >
      <div ref="uploadBody" class="w-100">
        <slot ref="uploadBody" :is-dragging="isDragging">
          <div
            v-if="(files.length === 0 || showEmptyState) && !customEmptyState"
            class="text-center dz-default dz-message"
          >
            <slot name="empty-state">
              Drag and drop files here to upload or click to browse your
              computer.
            </slot>
          </div>
          <div v-if="!showEmptyState">
            <li
              v-for="file in files"
              :key="file.upload.uuid"
              class="uploaded-dropzone-item"
            >
              <div class="d-flex justify-content-between">
                <div class="d-flex align-items-center item-wrapper">
                  <i class="fas fa-book mr-2"></i>
                  <span class="item-name">{{ file.name }}</span>
                </div>
                <a class="dz-remove" @click="removeFile(file)">
                  <i class="fas fa-times-circle" />
                </a>
              </div>
            </li>
          </div>
        </slot>
      </div>
      <div
        v-if="isDragging && $refs.uploadBody && $slots['drag-overlay']"
        class="drag-overlay d-flex flex-column justify-content-center align-items-center"
        data-testid="upload-drag-overlay"
        :style="{
          top: `${$refs.uploadBody.offsetTop}px`,
          left: `${$refs.uploadBody.offsetLeft}px`,
          width: `${$refs.uploadBody.offsetWidth}px`,
          height: `${$refs.uploadBody.offsetHeight}px`,
        }"
      >
        <img
          v-if="filesValid"
          class="mx-auto w-100 d-block"
          src="../../img/add-documents.svg"
        />
        <img
          v-else
          class="mx-auto w-100 d-block"
          src="../../img/invalid-documents.svg"
        />
        <h5 class="mt-3">
          <slot v-if="filesValid" name="drag-overlay" />
          <span v-else>One or more of your files can't be uploaded</span>
        </h5>
      </div>
    </vue-auto-dropzone>
    <div
      v-if="tagsInput"
      class="document-tags mt-2 d-flex flex-row align-items-start"
    >
      <tags-input
        id="upload"
        :value="selectedTags"
        :allow-create="true"
        :right="false"
        boundary="window"
        @input="onTagsChange"
      >
        <small v-if="selectedTags.length === 0"> Tag these documents </small>
      </tags-input>
      <tags id="upload" :tags="selectedTags" />
    </div>
    <b-progress
      v-if="!!showProgress && isProcessing"
      :value="uploadProgressPercentage"
      :max="100"
      class="mt-3"
      show-progress
      animated
    />
  </div>
</template>

<script>
import VueAutoDropzone from 'vue-auto-dropzone';
import axios from 'axios';
import { BASE_URL } from './../../environment';
import TagsInput from './TagsInput';
import Tags from '@/V2/documents/Tags';

export default {
  components: {
    tags: Tags,
    'tags-input': TagsInput,
    'vue-auto-dropzone': VueAutoDropzone,
  },
  props: {
    autoProcessQueue: {
      type: Boolean,
      default: false,
    },
    tagsInput: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      default: undefined,
    },
    acceptedFileTypes: {
      type: Array,
      default: () => [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'application/pdf',
      ],
    },
    customEmptyState: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 50,
    },
    showProgress: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 40,
    },
    includeStyling: {
      type: Boolean,
      default: true,
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    border: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => {},
    },
    showEmptyState: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropzoneOptions: {
        url: `${BASE_URL}${this.apiUrl}`,
        autoProcessQueue: this.autoProcessQueue,
        thumbnailWidth: 10,
        timeout: 1200000,
        parallelUploads: 40,
        maxFilesize: this.maxFileSize,
        uploadMultiple: true,
        maxFiles: this.maxFiles,
        acceptedFiles: this.acceptedFileTypes.join(','),
        addRemoveLinks: false,
        withCredentials: true,
        headers: {
          'X-CSRFToken': axios.defaults.headers.common['X-CSRFToken'],
        },
        ...this.options,
      },
      isDragging: false,
      isDraggingTimeoutId: null,
      filesValid: true,
      uploadProgressPercentage: 0,
      selectedTags: [],
    };
  },
  watch: {
    // vue-auto-dropzone does not provide a disabled prop, only a method, so we
    // need to call this based on prop updates, as well as on the initial mount.
    // https://github.com/Etheryte/vue-auto-dropzone#method-list
    disabled(newVal) {
      if (newVal) {
        this.$refs.documentsDropzone.disable();
      } else {
        this.$refs.documentsDropzone.enable();
      }
    },
  },
  mounted() {
    if (this.disabled) {
      this.$refs.documentsDropzone.disable();
    }
  },
  methods: {
    setUrl(url) {
      this.$refs.documentsDropzone.setOption('url', `${BASE_URL}${url}`);
    },
    sendingEvent(file, xhr, formData) {
      formData.append(
        'tags',
        JSON.stringify(this.selectedTags.map(({ value }) => ({ text: value }))),
      );
    },
    sendingMultipleEvent(files, xhr, formData) {
      formData.append(
        'upload_ids',
        JSON.stringify(files.map((file) => file.upload.uuid)),
      );
    },
    checkValidFiles(event) {
      const { items } = event.dataTransfer;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!this.acceptedFileTypes.includes(item.type)) {
          this.filesValid = false;
          event.preventDefault();
          return;
        }
      }

      if (!this.filesValid) {
        this.filesValid = true;
      }
    },
    onDrag(event) {
      this.isDragging = true;
      this.checkValidFiles(event);

      if (this.isDraggingTimeoutId) {
        clearTimeout(this.isDraggingTimeoutId);
      }

      this.isDraggingTimeoutId = setTimeout(() => {
        this.isDragging = false;
      }, 600);
    },
    dragStart() {
      this.isDragging = true;
      this.$emit('drag-start');
    },
    dragEnd() {
      this.isDragging = false;
      this.$emit('drag-end');
    },
    drop(event) {
      this.isDragging = false;
      this.checkValidFiles(event);
      this.$emit('drop');
    },
    startedProcessing() {
      this.$emit('is-processing', true);
    },
    uploadProgress(totalUploadProgress, totalBytes, totalBytesSent) {
      this.uploadProgressPercentage = totalUploadProgress;
    },
    fileUploadProgress(file, progress, totalBytesSent) {
      this.$emit('file-upload-progress', file, progress, totalBytesSent);
    },
    uploadError(files, response, xhr) {
      this.uploadProgressPercentage = 0;
      this.$emit('is-processing', false);
      this.$emit('upload-error', response.message || response, files);
    },
    uploadSuccess() {
      this.$store.commit('addTags', this.selectedTags);
      this.$emit('upload-success');
    },
    uploadCompleted() {
      this.uploadProgressPercentage = 0;
      this.$emit('upload-completed');
    },
    uploadFileSuccess(file, response) {
      this.$emit('upload-file-success', file, response);
    },
    performDeleteTag() {},
    cancelEdit() {},
    onTagsChange(newTagsValue) {
      this.selectedTags = newTagsValue.map(
        (tag) =>
          this.$store.state.tags.list.find(({ value }) => value === tag) || {
            value: tag,
          },
      );
    },
  },
};
</script>

<style lang="scss">
.document-tags {
  .form-group & .btn-group {
    width: unset;
  }
}
</style>
